import React, { useEffect, useState } from "react";
import { Trash2, Plus, Save } from "lucide-react";
import { toast } from "sonner";
import { listCoupons, createCoupon, deleteCoupon, updateCoupon } from "../api";
import { PageHeader } from "./Dashboard";
import RefreshButton from "../components/RefreshButton";

export default function Coupons() {
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState({ code: "", percent_off: 10, active: true, description: "" });
  const [loading, setLoading] = useState(true);

  const reload = () => {
    setLoading(true);
    listCoupons().then(setCoupons).finally(() => setLoading(false));
  };
  useEffect(reload, []);

  const onCreate = async (e) => {
    e.preventDefault();
    try {
      await createCoupon({
        ...form,
        code: form.code.toUpperCase(),
        percent_off: Number(form.percent_off),
      });
      toast.success(`Coupon ${form.code.toUpperCase()} created`);
      setForm({ code: "", percent_off: 10, active: true, description: "" });
      reload();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Create failed");
    }
  };

  const toggle = async (c) => {
    try {
      await updateCoupon(c.code, { ...c, active: !c.active });
      toast.success(`${c.code} ${!c.active ? "activated" : "paused"}`);
      reload();
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (code) => {
    if (!window.confirm(`Delete coupon ${code}?`)) return;
    try {
      await deleteCoupon(code);
      toast.success("Coupon deleted");
      reload();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="admin-coupons">
      <PageHeader eyebrow="Marketing" title="Coupons" sub="Promo codes available at checkout" />
      <div className="p-10 max-w-5xl">
        {/* Create form */}
        <section className="bg-[#161617] border border-[#2a2a2d] p-7 mb-8">
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#8c8a7e] mb-5">Create coupon</p>
          <form onSubmit={onCreate} className="grid grid-cols-1 md:grid-cols-[1fr_140px_1fr_auto] gap-4 items-end">
            <Field label="Code">
              <input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="WELCOME10"
                required
                data-testid="admin-coupon-code"
                className={input}
              />
            </Field>
            <Field label="% Off">
              <input
                type="number"
                min={1}
                max={90}
                value={form.percent_off}
                onChange={(e) => setForm({ ...form, percent_off: e.target.value })}
                required
                data-testid="admin-coupon-percent"
                className={input}
              />
            </Field>
            <Field label="Description">
              <input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Festive season offer"
                className={input}
              />
            </Field>
            <button type="submit" className="bg-[#c3ba9e] text-[#0d0d0e] px-6 py-2.5 text-[11px] tracking-[0.28em] uppercase font-medium hover:bg-[#FDFAF6] flex items-center gap-2" data-testid="admin-coupon-create">
              <Plus size={14} /> Create
            </button>
          </form>
        </section>

        {/* List */}
        <div className="flex items-center justify-end mb-3">
          <RefreshButton onClick={reload} loading={loading} testid="admin-coupons-refresh" />
        </div>
        <div className="bg-[#161617] border border-[#2a2a2d]">
          <div className="grid grid-cols-[1fr_120px_2fr_140px_120px] gap-4 px-6 py-4 border-b border-[#2a2a2d] text-[10px] tracking-[0.28em] uppercase text-[#8c8a7e]">
            <span>Code</span>
            <span>% Off</span>
            <span>Description</span>
            <span>Status</span>
            <span></span>
          </div>
          {loading ? (
            <p className="p-10 text-center text-[#8c8a7e] text-sm">Loading…</p>
          ) : coupons.length === 0 ? (
            <p className="p-10 text-center text-[#8c8a7e] text-sm">No coupons.</p>
          ) : (
            coupons.map((c) => (
              <div key={c.code} className="grid grid-cols-[1fr_120px_2fr_140px_120px] gap-4 px-6 py-4 border-b border-[#2a2a2d] items-center hover:bg-[#1a1a1c]" data-testid={`admin-coupon-row-${c.code}`}>
                <span className="font-mono text-[#c3ba9e] tracking-wider">{c.code}</span>
                <span className="text-[#e9e6db]">{c.percent_off}%</span>
                <span className="text-[12px] text-[#9a9787] truncate">{c.description || "—"}</span>
                <button
                  onClick={() => toggle(c)}
                  className={`text-[10px] tracking-[0.28em] uppercase px-3 py-1 border ${c.active ? "border-[#3d6b4f] text-[#7fb495] bg-[#1a2a20]" : "border-[#2a2a2d] text-[#5e5d54]"}`}
                  data-testid={`admin-coupon-toggle-${c.code}`}
                >
                  {c.active ? "Active" : "Paused"}
                </button>
                <div className="flex justify-end">
                  <button onClick={() => remove(c.code)} className="p-2 text-[#9a9787] hover:text-[#c97070]" data-testid={`admin-coupon-delete-${c.code}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

const input = "w-full bg-[#0d0d0e] border border-[#2a2a2d] focus:border-[#c3ba9e] outline-none px-3 py-2.5 text-[14px] text-[#e9e6db]";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] tracking-[0.28em] uppercase text-[#8c8a7e] mb-2 block">{label}</span>
      {children}
    </label>
  );
}
